<template>
  <div class="vendors-page">
    <div class="vendors-container">
      <div class="page-header">
        <div>
          <button @click="goBack" class="btn-back">
            <font-awesome-icon icon="fa-solid fa-arrow-left" />
            {{ $t('companies.backToCompanies') }}
          </button>
          <h1 class="page-title">{{ company?.name }} - {{ $t('vendors.title') }}</h1>
          <p class="page-subtitle">{{ $t('vendors.subtitle', { company: company?.name }) }}</p>
        </div>
        <button @click="showCreateModal = true" class="btn-create">
          <font-awesome-icon icon="fa-solid fa-plus" />
          {{ $t('vendors.addVendor') }}
        </button>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>{{ $t('common.loading') }}</p>
      </div>

      <div v-else-if="vendors.length === 0" class="empty-state">
        <font-awesome-icon icon="fa-solid fa-users" style="font-size: 80px; color: #cbd5e1;" />
        <h3>{{ $t('vendors.noVendors') }}</h3>
        <p>{{ $t('vendors.noVendorsDesc') }}</p>
        <button @click="showCreateModal = true" class="btn-primary">
          <font-awesome-icon icon="fa-solid fa-plus" />
          {{ $t('vendors.addVendor') }}
        </button>
      </div>

      <div v-else class="vendors-list">
        <div v-for="vendor in vendors" :key="vendor.id" class="vendor-card">
          <div class="vendor-header">
            <h3 class="vendor-name">{{ vendor.vendor_name }}</h3>
            <button @click="editVendor(vendor)" class="btn-icon" :title="$t('common.edit')">
              <font-awesome-icon icon="fa-solid fa-pen" />
            </button>
          </div>
          <div class="vendor-details">
            <div v-if="vendor.vendor_vat" class="detail-row">
              <span class="detail-label">{{ $t('vendors.vatNumber') }}:</span>
              <span class="detail-value">{{ vendor.vendor_vat }}</span>
            </div>
            <div v-if="vendor.vendor_address" class="detail-row">
              <span class="detail-label">{{ $t('vendors.address') }}:</span>
              <span class="detail-value">{{ vendor.vendor_address }}</span>
            </div>
            <div v-if="vendor.vendor_email" class="detail-row">
              <span class="detail-label">Email:</span>
              <span class="detail-value">{{ vendor.vendor_email }}</span>
            </div>
            <div v-if="vendor.vendor_phone" class="detail-row">
              <span class="detail-label">Phone:</span>
              <span class="detail-value">{{ vendor.vendor_phone }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Vendor Modal -->
    <VendorModal
      v-if="showCreateModal || editingVendor"
      :vendor="editingVendor"
      :companyId="companyId"
      @close="closeModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import api from '@/utils/api';
import VendorModal from '@/components/companies/VendorModal.vue';

const router = useRouter();
const route = useRoute();

const companyId = computed(() => route.params.companyId);
const company = ref(null);
const vendors = ref([]);
const loading = ref(true);
const showCreateModal = ref(false);
const editingVendor = ref(null);

const loadCompany = async () => {
  try {
    const response = await api.get(`/api/companies/${companyId.value}`);
    company.value = response.data;
  } catch (error) {
    console.error('Failed to load company:', error);
  }
};

const loadVendors = async () => {
  try {
    loading.value = true;
    const response = await api.get(`/api/companies/${companyId.value}/vendors`);
    vendors.value = response.data.data || response.data;
  } catch (error) {
    console.error('Failed to load vendors:', error);
  } finally {
    loading.value = false;
  }
};

const editVendor = (vendor) => {
  editingVendor.value = vendor;
};

const closeModal = () => {
  showCreateModal.value = false;
  editingVendor.value = null;
};

const handleSaved = () => {
  closeModal();
  loadVendors();
};

const goBack = () => {
  router.push('/companies');
};

onMounted(() => {
  loadCompany();
  loadVendors();
});
</script>

<style scoped>
.vendors-page {
  min-height: 100vh;
  background: var(--bg-gray);
  padding: 24px 20px;
}

.vendors-container {
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  gap: 20px;
}

.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 14px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 12px;
}

.btn-back:hover {
  background: var(--bg-gray);
  color: var(--text-primary);
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.page-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

.btn-create {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.btn-create:hover {
  background: var(--primary-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state svg {
  margin-bottom: 24px;
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.empty-state p {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 24px 0;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary:hover {
  background: var(--primary-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.vendors-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
}

.vendor-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--border);
  transition: all 0.2s ease;
}

.vendor-card:hover {
  border-color: var(--primary);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.1);
  transform: translateY(-2px);
}

.vendor-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border);
}

.vendor-name {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  flex: 1;
}

.btn-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-gray);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-icon:hover {
  background: var(--primary-light);
  border-color: var(--primary);
  color: var(--primary);
}

.vendor-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-row {
  display: flex;
  gap: 8px;
}

.detail-label {
  font-size: 13px;
  color: var(--text-secondary);
  font-weight: 500;
  min-width: 60px;
}

.detail-value {
  font-size: 13px;
  color: var(--text-primary);
  flex: 1;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .vendors-list {
    grid-template-columns: 1fr;
  }
}
</style>
